import Address from './Address';
import Util from './Util';

/**
 * Represents an address-input via a google-map. It renders a given address on the map with a marker and displays the
 * address-information on an info-window. It also offers the possibility to change the address by clicking on the
 * map or moving the address-marker.
 * It triggers the following Google API events:
 * - 'input_changed': when the address-marker was moved and thus the address-input is changing
 * - 'position_changed': when the address changed e.g. when the address-marker was moved to another place or the map was
 *                      clicked.
 */
export default class AddressMap {

    /**
     * Sets up the map, marker and infowindow and their events.
     * @param {Object} canvas element to render the map in.
     * @param {Object} position default geo-coordinate to render the map at.
     * @param {int} zoomLevel default zoom-level to render the map with.
     */
    constructor(canvas, position, zoomLevel) {
        AddressMap.MAXIMUM_ZOOM = 16;
        AddressMap.MINIMUM_ZOOM = 12;
        AddressMap.MINIMUM_METERS_PER_PIXEL = 1;
        const self = this;

        // class members
        this.position = position;
        this.defaultPosition = position;
        this.canvas = canvas;

        // setup map
        this.map = new google.maps.Map(canvas, {
            center: this.defaultPosition,
            zoom: zoomLevel,
            draggableCursor: 'pointer'
        });

        // setup info-window
        this.info = new google.maps.InfoWindow({disableAutoPan: true});

        // setup perimeter display
        this.perimeter = new google.maps.Circle({
            clickable: false,
            fillColor: '#0055ff',
            fillOpacity: 0.2,
            strokeColor: '#0055ff',
            strokeOpacity: 0.4,
            strokeWeight: 1,
            zIndex: 1
        });

        // setup address-marker
        this.marker = new google.maps.Marker({draggable: true});

        // start dragging the marker
        this.marker.addListener('dragstart', () => {
            this.info.close();
            google.maps.event.trigger(this, 'input_changed');
        });

        // change address on marker-drag
        this.marker.addListener('dragend', function () {
            let position = this.getPosition();
            self.setPosition(position);
        });

        // open info-window by clicking on the marker
        this.marker.addListener('click', () => {
            if (!Util.isEmpty(this.info.getContent())) {
                this.info.open(this.map, this.marker);
            }
        });

        // change address on map-click
        this.map.addListener('click', (event) => {
            let position = event.latLng;
            this.setPosition(position);
        });
    }

    /**
     * Set the address; if it is not given, clear the map.
     * @param {Address|null} address the address or null
     * @returns {null} null
     */
    setAddress(address = null) {
        if (Util.isSet(address) && address instanceof Address) {
            if (this.position !== address.query && address.hasOwnProperty('geometry') &&
                    address.geometry.hasOwnProperty('location')) {
                // set the new position
                this.position = address.geometry.location;
                this.info.close();
                this.perimeter.setMap(null);
                this.setViewport(address.geometry.viewport, this.position);
                this.marker.setPosition(this.position);
                this.marker.setMap(this.map);
            }
            // setup info-window content
            let infoContent = address.toString(true);
            if (!Util.isEmpty(infoContent)) {
                this.info.setContent(infoContent);
                this.info.open(this.map, this.marker);
            }
        } else {
            // clear the map on empty address
            this.clear();
        }
    }

    /**
     * Clears the map by closing the info-window and hiding the perimeter-circle and marker.
     * @returns {null} null
     */
    clear() {
        this.info.close();
        this.perimeter.setMap(null);
        this.marker.setMap(null);
    }

    /**
     * Set a new position for the map where the position needs to be a google.maps.LatLng instance.
     * @param {google.maps.LatLng} position the position from google
     * @returns {null} null
     */
    setPosition(position) {
        if (position instanceof google.maps.LatLng) {
            this.position = position;
            this.info.close();
            this.perimeter.setMap(null);
            this.map.panTo(this.position);
            this.marker.setPosition(this.position);
            this.marker.setMap(this.map);
            google.maps.event.trigger(this, 'position_changed', this.position);
        }
    }

    /**
     * Set a new perimeter where the perimeter needs to be a google.maps.Circle instance.
     * @param {google.maps.Circle} perimeter the perimiter from google
     * @returns {null} null
     */
    setPerimeter(perimeter) {
        if (perimeter instanceof google.maps.Circle) {
            this.clear();
            this.perimeter.setCenter(perimeter.getCenter());
            this.perimeter.setRadius(perimeter.getRadius());
            this.perimeter.setMap(this.map);
            this.setViewport(this.perimeter.getBounds(), this.perimeter.getCenter());
        }
    }

    /**
     * Set the map to the given viewport if it is valid and within range.
     * @param {google.maps.LatLngBounds} viewport to set the map bounds to if applicable
     * @param {google.maps.LatLng|google.maps.LatLngLiteral} fallbackPosition fallback-position
     * @returns {null} null
     */
    setViewport(viewport, fallbackPosition) {
        if (viewport !== null && typeof viewport !== 'undefined' &&
                viewport.hasOwnProperty('south') && viewport.hasOwnProperty('west') &&
                viewport.hasOwnProperty('north') && viewport.hasOwnProperty('east')) {
            viewport = new google.maps.LatLngBounds({lat: viewport.south, lng: viewport.west},
                {lat: viewport.north, lng: viewport.east});
        }
        if (viewport !== null && viewport instanceof google.maps.LatLngBounds) {
            // use default zoom if the meters shown per pixel are too small
            const metersPerPixel = this.getMetersPerPixel(viewport);
            if (metersPerPixel <= AddressMap.MINIMUM_METERS_PER_PIXEL) {
                this.map.setZoom(AddressMap.MAXIMUM_ZOOM);
                this.map.panTo(fallbackPosition);
            } else {
                this.map.fitBounds(viewport);
            }
        } else {
            this.map.panTo(fallbackPosition);
            this.map.setZoom(AddressMap.MINIMUM_ZOOM);
        }
    }

    /**
     * Calculates the meters shown per pixel for the given bounds
     * @param {google.maps.LatLngBounds} bounds bounds to calculate the meters per pixel for
     * @returns {number} meters shown per pixel for the given bounds
     */
    getMetersPerPixel(bounds) {
        let metersPerPixel = 0;
        if (bounds instanceof google.maps.LatLngBounds) {
            let ne = bounds.getNorthEast();
            let sw = bounds.getSouthWest();
            let diaDistance = google.maps.geometry.spherical.computeDistanceBetween(ne, sw);
            let htmlWidth = this.canvas.offsetWidth;
            let htmlHeight = this.canvas.offsetHeight;
            Math.hypot = Math.hypot || function (x, y) {
                    return Math.sqrt(x * x + y * y);
                };
            let diaPixel = Math.hypot(htmlWidth, htmlHeight);
            metersPerPixel = (diaDistance / diaPixel);
        }
        return metersPerPixel;
    }
}